import * as THREE from 'three/webgpu';
import type { FeatureCurve } from '../../mesh/feature';
import { normalizeVec3 } from './PointFeatureSolveContextGeometryUtils';
import type { PointFeatureSolveGeometryData } from './PointFeatureSolveGeometryPayloadTypes';
import { readFeatureCurveWorldSamplePoints } from './ReferenceCircularEdgeUtils';
import { resolveCircularGeometryFromSamplePoints } from '../../utils/geometry';

export { readFeatureCurveWorldSamplePoints, resolveCircularSolvePoints } from './ReferenceCircularEdgeUtils';

/**
 * 计算圆边采样点在参考平面上的有符号扫角。
 *
 * @param sampledPoints 曲线采样点。
 * @param center 圆心。
 * @param xAxis 圆参数系 X 轴。
 * @param yAxis 圆参数系 Y 轴。
 * @returns 有符号扫角。
 */
export function computeCircularSweepAngle(
  sampledPoints: readonly THREE.Vector3[],
  center: THREE.Vector3,
  xAxis: THREE.Vector3,
  yAxis: THREE.Vector3,
): number {
  const epsilon = 1e-6;
  if (sampledPoints.length < 2) {
    return 0;
  }

  let previousAngle: number | null = null;
  let accumulatedAngle = 0;
  let firstNonZeroDelta = 0;

  sampledPoints.forEach((samplePoint) => {
    const radialDirection = normalizeVec3(samplePoint.clone().sub(center));
    if (!radialDirection) {
      return;
    }

    const currentAngle = Math.atan2(
      radialDirection.dot(yAxis),
      radialDirection.dot(xAxis),
    );
    if (previousAngle === null) {
      previousAngle = currentAngle;
      return;
    }

    let deltaAngle = currentAngle - previousAngle;
    if (deltaAngle <= -Math.PI) {
      deltaAngle += 2 * Math.PI;
    } else if (deltaAngle > Math.PI) {
      deltaAngle -= 2 * Math.PI;
    }

    if (Math.abs(deltaAngle) > epsilon && Math.abs(firstNonZeroDelta) <= epsilon) {
      firstNonZeroDelta = deltaAngle;
    }

    accumulatedAngle += deltaAngle;
    previousAngle = currentAngle;
  });

  if (Math.abs(accumulatedAngle) > epsilon) {
    return accumulatedAngle;
  }

  const startPoint = sampledPoints[0] ?? null;
  const endPoint = sampledPoints[sampledPoints.length - 1] ?? null;
  if (
    startPoint
    && endPoint
    && startPoint.distanceTo(endPoint) <= epsilon
    && Math.abs(firstNonZeroDelta) > epsilon
  ) {
    return Math.sign(firstNonZeroDelta) * 2 * Math.PI;
  }

  return accumulatedAngle;
}

/**
 * 解析圆形特征曲线的基准点候选几何载荷。
 *
 * @param curve 特征曲线对象。
 * @returns 圆边几何载荷；解析失败时返回 null。
 */
export function resolveCircularEdgePayload(curve: FeatureCurve): PointFeatureSolveGeometryData | null {
  const sampledPoints = readFeatureCurveWorldSamplePoints(curve);
  // 复用底层圆几何计算，基准点保留按有序采样确定扫角及顺逆时针的既有语义。
  const circle = resolveCircularGeometryFromSamplePoints(sampledPoints);
  if (!circle) return null;
  const { center, normal: planeNormal, radius } = circle;
  const startPoint = sampledPoints[0]?.clone() ?? null;
  const endPoint = sampledPoints[sampledPoints.length - 1]?.clone() ?? null;
  if (!startPoint || !endPoint) {
    return null;
  }

  const radialXAxis = normalizeVec3(startPoint.clone().sub(center));
  if (!radialXAxis) return null;
  const radialYAxis = normalizeVec3(planeNormal.clone().cross(radialXAxis));
  if (!radialYAxis) {
    return null;
  }

  const signedSweepAngle = computeCircularSweepAngle(sampledPoints, center, radialXAxis, radialYAxis);
  const angleRange = Math.abs(signedSweepAngle) > 1e-6 ? Math.abs(signedSweepAngle) : 2 * Math.PI;

  return {
    center,
    radius,
    startPoint,
    endPoint,
    normal: planeNormal,
    xAxis: radialXAxis,
    yAxis: radialYAxis,
    angleRange,
    clockwise: signedSweepAngle < 0,
    length: radius * angleRange,
  };
}
